import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getOrganizationId } from "../utils/organizationUtils";
import {
  DEFAULT_EXPIRY_THRESHOLD_MONTHS,
  getPathExpiryStatus,
  isNearExpiryDate,
  monthsToThresholdDays,
  PATH_EXPIRY_THRESHOLD_CHANGED,
  parseExpiryThresholdMonths,
} from "../utils/pathExpiryThreshold";
import {
  getPathExpiryThresholdMonths,
  savePathExpiryThresholdMonths,
} from "../services/pathSettingsService";

export const usePathExpiryThreshold = () => {
  const { user } = useAuth();
  const organizationId = getOrganizationId(user);
  const [months, setMonths] = useState(DEFAULT_EXPIRY_THRESHOLD_MONTHS);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const value = await getPathExpiryThresholdMonths(organizationId);
      setMonths(value);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onThresholdChanged = (event) => {
      const next = parseExpiryThresholdMonths(event?.detail?.months, null);
      if (next != null) {
        setMonths(next);
      } else {
        reload();
      }
    };
    window.addEventListener(PATH_EXPIRY_THRESHOLD_CHANGED, onThresholdChanged);
    return () => {
      window.removeEventListener(PATH_EXPIRY_THRESHOLD_CHANGED, onThresholdChanged);
    };
  }, [reload]);

  const saveThreshold = useCallback(
    async (newMonths) => {
      const saved = await savePathExpiryThresholdMonths(organizationId, newMonths);
      setMonths(saved);
      return saved;
    },
    [organizationId]
  );

  const thresholdDays = useMemo(() => monthsToThresholdDays(months), [months]);

  const isNearExpiry = useCallback(
    (expiryDate) => isNearExpiryDate(expiryDate, months),
    [months]
  );

  const getExpiryStatus = useCallback(
    (expiryDate) => getPathExpiryStatus(expiryDate, months),
    [months]
  );

  return {
    months,
    thresholdDays,
    loading,
    reload,
    saveThreshold,
    isNearExpiry,
    getExpiryStatus,
  };
};
